import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, X, Code, Save, Loader2, ExternalLink, AlertTriangle, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import EmbedRenderer from "@/components/EmbedRenderer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
const headers = () => {
  const t = localStorage.getItem("auth_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * Dedicated Embeds dashboard page.
 *
 * Lets a business paste any HTML/iframe/script embed code (LeadConnector
 * Review Widget, Google Reviews, Calendly, etc.) into a titled slot. Saved
 * embeds publish onto their public listing under the "Embeds" content block.
 */
export default function EmbedsPage() {
  const [listing, setListing] = useState(null);
  const [embeds, setEmbeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(null);

  const fetchListing = async () => {
    try {
      const r = await axios.get(`${API}/listings/my`, { headers: headers() });
      const lst = r.data?.listings?.[0] || r.data;
      setListing(lst);
      setEmbeds(lst?.custom_embeds || []);
    } catch (e) {
      toast.error("Couldn't load your listing.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchListing(); }, []);

  const add = () => {
    setEmbeds((p) => [...p, { id: Date.now().toString(), title: "", code: "" }]);
  };

  const update = (idx, field, value) => {
    setEmbeds((p) => p.map((e, i) => (i === idx ? { ...e, [field]: value } : e)));
  };

  const remove = (idx) => {
    if (!window.confirm("Delete this embed? This cannot be undone.")) return;
    setEmbeds((p) => p.filter((_, i) => i !== idx));
  };

  const save = async () => {
    if (!listing?.id) return;
    setSaving(true);
    try {
      // Drop empties before saving
      const clean = embeds.filter((e) => (e.title || "").trim() || (e.code || "").trim());
      await axios.patch(
        `${API}/listings/${listing.id}`,
        { custom_embeds: clean },
        { headers: headers() },
      );
      toast.success("Embeds saved — they're now live on your listing.");
      setEmbeds(clean);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-[#94A3B8]">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="embeds-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Code className="w-6 h-6 text-[#7C3AED]" /> Embeds
          </h1>
          <p className="text-[#94A3B8] text-sm mt-1 max-w-2xl">
            Paste embed codes from review widgets, booking tools, calendars, or any third-party widget. Saved embeds appear on your public listing.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {listing?.slug && (
            <a href={`/business/${listing.slug}#custom-embeds`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-white/15 bg-white/5 hover:bg-white/10 text-white">
                <ExternalLink className="w-4 h-4 mr-1.5" /> View on listing
              </Button>
            </a>
          )}
          <Button onClick={add} variant="outline" className="border-white/20 text-white hover:bg-white/5" data-testid="embed-add-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add Embed
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-gradient-to-r from-[#7C3AED] to-[#EC4899] hover:opacity-90 text-white font-semibold"
            data-testid="embed-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            {saving ? "Saving…" : "Save & Publish"}
          </Button>
        </div>
      </header>

      <div className="rounded-xl border border-[#7C3AED]/30 bg-[#7C3AED]/[0.05] p-4 text-sm text-[#C7D2E0] flex items-start gap-3" data-testid="embed-help">
        <AlertTriangle className="w-4 h-4 text-[#FBBF24] mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-white font-medium mb-1">Trusted widgets only</p>
          <p className="leading-relaxed">
            Only paste embed code from services you trust (LeadConnector review widget, Google Reviews, Calendly, Elfsight, etc.).
            Embeds run in your visitors' browsers — bad code can break your listing. Click <strong>Preview</strong> on any embed below to see how it renders before saving.
          </p>
        </div>
      </div>

      {embeds.length === 0 ? (
        <EmptyState onAdd={add} />
      ) : (
        <div className="space-y-4">
          {embeds.map((embed, idx) => (
            <div
              key={embed.id || idx}
              className="rounded-xl border border-white/10 bg-white/[0.03] p-5"
              data-testid={`embed-slot-${idx}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <Input
                  value={embed.title || ""}
                  onChange={(e) => update(idx, "title", e.target.value)}
                  placeholder="Embed title (e.g. Video Testimonials, Reviews, Book a Call)"
                  className="bg-[#0F172A] border-white/10 text-white"
                  data-testid={`embed-title-${idx}`}
                />
                <button onClick={() => remove(idx)} className="text-[#64748B] hover:text-red-400 mt-2" aria-label="Delete embed" data-testid={`embed-remove-${idx}`}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              <label className="block text-xs uppercase tracking-widest text-[#94A3B8] mb-1.5 font-semibold">Embed code</label>
              <textarea
                value={embed.code || ""}
                onChange={(e) => update(idx, "code", e.target.value)}
                placeholder="Paste the full embed snippet here (HTML / iframe / script). Example:&#10;&#10;&lt;script src='https://reputationhub.site/...'&gt;&lt;/script&gt;&#10;&lt;iframe src='https://...'&gt;&lt;/iframe&gt;"
                rows={6}
                className="w-full px-3 py-2 bg-[#020817] border border-white/10 rounded-md text-white font-mono text-xs placeholder:text-[#475569] focus:outline-none focus:border-[#7C3AED]"
                data-testid={`embed-code-${idx}`}
              />
              <div className="mt-3 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => setPreviewIdx(previewIdx === idx ? null : idx)}
                  className="inline-flex items-center gap-1.5 text-xs text-[#7C3AED] hover:text-[#A78BFA] font-medium"
                  data-testid={`embed-preview-${idx}`}
                >
                  <Eye className="w-3.5 h-3.5" />
                  {previewIdx === idx ? "Hide preview" : "Live preview"}
                </button>
                <span className="text-[10px] text-[#64748B]">
                  {(embed.code || "").length} characters
                </span>
              </div>
              {previewIdx === idx && embed.code && (
                <div className="mt-3 rounded-lg border border-dashed border-white/15 bg-white p-3" data-testid={`embed-preview-area-${idx}`}>
                  <p className="text-xs text-[#64748B] mb-2 font-mono">live preview ↓</p>
                  <EmbedRenderer html={embed.code} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center">
      <Code className="w-12 h-12 mx-auto text-[#7C3AED]/60 mb-4" />
      <h3 className="text-white text-lg font-semibold mb-1">No embeds yet</h3>
      <p className="text-[#94A3B8] mb-5 max-w-md mx-auto">
        Add a review widget, booking calendar, video testimonial, or any other third-party widget. They'll show on your public listing.
      </p>
      <Button onClick={onAdd} className="bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white font-semibold rounded-full" data-testid="embeds-empty-add">
        <Plus className="w-4 h-4 mr-1.5" /> Add Your First Embed
      </Button>
    </div>
  );
}
